import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Button } from 'reactstrap';
import { useIntl } from 'react-intl';

import { setUserSetting } from '../../actions/Auth';
import {
  clearFilesFiltersAndReload,
  clearFilesList,
  fetchFiles,
  setFilesFilters,
  setFilesParams,
} from '../../actions/Files';

import { APIupdateUserSettings } from '../../api/User';

import SearchInputSearches from './SearchInputSearches';

import { uuidPattern } from '../../helpers';
import { addFlashMessage } from '../../helpers/FlashMessages';
import { getPinnedSearchSuggestedName } from '../../helpers/Search';
import { getUserSetting } from '../../helpers/Settings';
import {
  getCurrentURLParams,
  handleURLParams,
  prepareSearchFilters,
} from '../../helpers/URLParams';

const SearchNoOptions = (props) => {
  const {
    history,
    location,
    updateFilesFilters,
    resetFilesFilters,
    updateUserSetting,
    value,
    search,
    searched,
    profile,
    show,
  } = props;
  const intl = useIntl();

  const [loading, setLoading] = useState(false);

  const handleClearLatestSearches = async () => {
    setLoading(true);
    await APIupdateUserSettings(profile.uuid, { latest_searches: [] });
    setLoading(false);
    addFlashMessage({
      type: 'success',
      text: intl.formatMessage({ defaultMessage: 'Cleared latest searches' }),
    });
    updateUserSetting({ latest_searches: [] });
  };

  const isFilesSearchesLocation = location.pathname === '/files' ||
    location.pathname.match(`/collections/(${uuidPattern})`);
  const isCollectionsSearchesLocation = location.pathname === '/collections';

  const handleFilters = (filters, type) => {
    const newFilters = prepareSearchFilters(filters);
    document.activeElement.blur();

    if (type === 'files') {
      resetFilesFilters();
      updateFilesFilters(newFilters);

      if (isFilesSearchesLocation) {
        handleURLParams(newFilters, history);
      } else {
        history.push({
          pathname: '/results',
          search: getCurrentURLParams({ search: newFilters }),
        });
      }
    } else if (type === 'collections') {
      if (isCollectionsSearchesLocation) {
        handleURLParams(newFilters, history);
      } else {
        history.push({
          pathname: '/collections',
          search: getCurrentURLParams({ search: newFilters }),
        });
      }
    }
  };

  const pinnedSearches = getUserSetting('pinned_searches') || [];
  const pinnedSearchesFiles = pinnedSearches.filter(s => s.type === 'files');
  const pinnedSearchesCollections = pinnedSearches.filter(s => s.type === 'collections');
  const latestSearches = getUserSetting('latest_searches') || [];

  if (profile && !profile.uuid) {
    return (
      <div className="search-login">
        {intl.formatMessage({ defaultMessage: '<link>Login</link> to search' }, {
          link: (msg) => <Link to="/login">{msg}</Link>,
        })}
      </div>
    );
  }

  if (show && !value) {
    return (
      <div className="search-input-searches-wrapper">
        {(search && searched) && (
          <div className="search-input-nothing-found">
            <h3>{intl.formatMessage({ defaultMessage: 'Nothing found' })}</h3>
            <hr />
          </div>
        )}
        {pinnedSearches.length > 0 && (
          <>
            <div className="search-input-pinned-searches-header">
              <h3>{intl.formatMessage({ defaultMessage: 'My pinned searches' })}</h3>
            </div>
            {/* eslint-disable jsx-a11y/click-events-have-key-events */}
            {/* eslint-disable jsx-a11y/no-static-element-interactions */}
            {pinnedSearchesFiles.length > 0 && (
              <div className="search-input-pinned-searches">
                {pinnedSearchesFiles.map((s) => (
                  <SearchInputSearches
                    search={s}
                    onClick={handleFilters}
                    key={s.uuid}
                    transparent={true}
                  />
                ))}
              </div>
            )}
            {pinnedSearchesCollections.length > 0 && (
              <>
                <div className="search-input-pinned-searches-header search-input-pinned-searches-header-spaced">
                  <h4>{intl.formatMessage({ defaultMessage: 'Collections pinned searches' })}</h4>
                </div>
                <div className="search-input-pinned-searches">
                  {pinnedSearchesCollections.filter(s => s.type === 'collections').map((s) => (
                    <SearchInputSearches
                      search={s}
                      onClick={handleFilters}
                      key={s.uuid}
                      transparent={true}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
        {latestSearches.length > 0 && (
          <>
            <div className="search-input-pinned-searches-header justify-sb">
              <h3>{intl.formatMessage({ defaultMessage: 'My latest searches' })}</h3>
              <Button
                color="link"
                size="sm"
                disabled={loading}
                onClick={handleClearLatestSearches}
              >
                {intl.formatMessage({ defaultMessage: 'Clear all' })}
              </Button>
            </div>
            <div className="search-input-pinned-searches">
              {latestSearches.map((s) => {
                const latestSearch = {
                  name: getPinnedSearchSuggestedName(s.filters),
                  ...s,
                };
                return (
                  <SearchInputSearches
                    search={latestSearch}
                    onClick={handleFilters}
                    pin={false}
                    key={s.uuid}
                  />
                );
              })}
            </div>
          </>
        )}
        {/* eslint-enable jsx-a11y/no-static-element-interactions */}
        {/* eslint-enable jsx-a11y/click-events-have-key-events */}
      </div>
    );
  }

  return null;
};

const mapStateToProps = state => ({
  profile: state.Auth.profile,
});

const mapDispatchToProps = dispatch => ({
  resetFilesFilters: () => {
    dispatch(clearFilesFiltersAndReload());
  },
  updateFilesFilters: (updatedFilters) => {
    dispatch(setFilesFilters(updatedFilters));
    dispatch(clearFilesList());
    dispatch(setFilesParams({ page: 0 }));
    dispatch(fetchFiles());
  },
  updateUserSetting: (setting) => {
    dispatch(setUserSetting(setting));
  },
});

SearchNoOptions.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  updateFilesFilters: PropTypes.func.isRequired,
  resetFilesFilters: PropTypes.func.isRequired,
  updateUserSetting: PropTypes.func.isRequired,
  show: PropTypes.bool.isRequired,
  profile: PropTypes.object,
  value: PropTypes.string,
  search: PropTypes.string,
  searched: PropTypes.bool,
};

SearchNoOptions.defaultProps = {
  profile: null,
  value: '',
  search: '',
  searched: false,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SearchNoOptions));
